/* BiometricSheet — bottom-sheet that shows the user their biometric (Face ID
   / Fingerprint) devices and lets them register the current device or
   revoke an old one. Opened from /account.

   Visual language unified with EventForm / ClientForm / AmendmentAssistant. */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { CaretRight, Fingerprint, Spinner, Trash, Check, WarningCircle, Plus } from "@phosphor-icons/react";
import {
  isWebAuthnSupported, isPlatformAuthenticatorAvailable,
  enableBiometric, listBiometricDevices, revokeBiometricDevice,
} from "../../lib/webauthnApi";
import { getUserToken } from "../../lib/userApi";

const CARD = "bg-white/80 backdrop-blur-md rounded-2xl border border-white/60";

export default function BiometricSheet({ onClose }) {
  const [supported, setSupported] = useState(null); // null = checking
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [revoking, setRevoking] = useState(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isWebAuthnSupported()) { setSupported(false); setLoading(false); return; }
      const ok = await isPlatformAuthenticatorAvailable();
      if (!alive) return;
      setSupported(ok);
      try {
        const list = await listBiometricDevices(getUserToken());
        if (alive) setDevices(list);
      } catch {
        if (alive) setDevices([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const refresh = async () => {
    try { setDevices(await listBiometricDevices(getUserToken())); } catch { /* */ }
  };

  const onEnable = async () => {
    setEnrolling(true);
    try {
      await enableBiometric({ token: getUserToken() });
      toast.success("تَمّ تَفعيل البَصمَة عَلى هذا الجِهاز");
      await refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "فَشِلَ تَفعيل البَصمَة";
      toast.error(msg);
    } finally {
      setEnrolling(false);
    }
  };

  const onRevoke = async (id) => {
    if (!window.confirm("هَل أَنت مُتَأَكِّد مِن إِنهاء هذا الجِهاز؟")) return;
    setRevoking(id);
    try {
      await revokeBiometricDevice(getUserToken(), id);
      toast.success("تَمَّ إنهاء الجِهاز");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تَعَذَّر الإنهاء");
    } finally {
      setRevoking(null);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/45 backdrop-blur-md flex items-stretch sm:items-center justify-center sm:p-6"
      onClick={onClose}
      dir="rtl"
      data-testid="biometric-sheet"
    >
      <div
        className="relative w-full sm:max-w-md h-[100dvh] sm:h-auto sm:max-h-[calc(100dvh-3rem)] sm:rounded-[28px] overflow-hidden flex flex-col font-tajawal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background:
            "linear-gradient(180deg,#FFFFFF 0%,#FAFAFA 28%,#F3F3F3 58%,#ECECEC 100%)",
        }}
      >
        {/* Sticky glass header */}
        <header
          className="px-4 pb-3 flex items-center gap-3 border-b border-[#0A192F]/8"
          style={{
            paddingTop: "calc(env(safe-area-inset-top, 0px) + 14px)",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.72) 100%)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="رجوع"
            data-testid="biometric-sheet-close"
            className="w-11 h-11 rounded-full bg-white shadow-[0_2px_8px_rgba(10,25,47,0.10)] flex items-center justify-center active:scale-95 transition-transform shrink-0"
          >
            <CaretRight size={22} weight="bold" className="text-[#0A192F]" />
          </button>
          <div className="flex-1 text-center min-w-0">
            <p className="text-[10px] text-[#B8860B] font-bold tracking-[0.18em]">أَمان</p>
            <h2 className="font-tajawal text-[18px] font-bold text-[#0A192F] leading-tight mt-0.5 truncate">
              الدُّخول بِالبَصمَة
            </h2>
          </div>
          <div className="w-11 h-11 shrink-0" aria-hidden="true" />
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3.5">
          {/* Hero / explainer */}
          <div className={`${CARD} px-5 py-5 text-center`}>
            <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[#FFF8E1] to-[#FCE99B] flex items-center justify-center">
              <Fingerprint size={28} weight="duotone" className="text-[#B8860B]" />
            </div>
            <h3 className="font-tajawal text-[16px] font-bold text-[#0A192F] mt-3">
              ادخُل بِبَصمَة الوَجه أو الإصبَع
            </h3>
            <p className="text-[12.5px] text-[#5A6470] mt-1.5 leading-relaxed">
              لا كَلِمَة سِرّ بَعدَ اليَوم. مِفتاحُك يُخَزَّن داخل جِهازِك فَقَط
              وَلا يَخرُج مِنه أَبَداً — حَتّى LEXIQ لا يَستَطيع رُؤيَتَه.
            </p>
          </div>

          {/* Support check */}
          {supported === false && (
            <div className={`${CARD} px-5 py-5 text-center border-[#FBBF24]/40 bg-[#FFFBEB]/80`}>
              <WarningCircle size={28} weight="duotone" className="mx-auto text-[#92400E]" />
              <p className="text-[14px] font-bold text-[#92400E] mt-2">جِهازُك لا يَدعَم البَصمَة</p>
              <p className="text-[12px] text-[#92400E]/80 mt-1">
                يَحتاج Face ID / Touch ID أو بَصمَة Windows Hello / Android.
              </p>
            </div>
          )}

          {/* Enable button */}
          {supported && (
            <button
              type="button"
              onClick={onEnable}
              disabled={enrolling}
              data-testid="biometric-enable-btn"
              className="w-full px-4 py-3 text-[15px] font-bold text-white rounded-full active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
              style={{
                background: "linear-gradient(135deg,#E6C463 0%,#B8860B 100%)",
                boxShadow: "0 6px 18px rgba(212,175,55,0.35)",
              }}
            >
              {enrolling ? (
                <>
                  <Spinner size={16} className="animate-spin" />
                  <span>اِنظُر إلى الكاميرا…</span>
                </>
              ) : (
                <>
                  <Plus size={16} weight="bold" />
                  <span>تَفعيل البَصمَة عَلى هذا الجِهاز</span>
                </>
              )}
            </button>
          )}

          {/* Devices list */}
          <div className="pt-2">
            <h4 className="text-[12px] font-bold tracking-[0.18em] text-[#B8860B] mb-2">
              الأَجهِزَة المُسَجَّلَة
            </h4>
            {loading ? (
              <div className="py-6 text-center text-[#5A6470] text-[13px]">جاري التَّحميل…</div>
            ) : devices.length === 0 ? (
              <div className={`${CARD} px-5 py-6 text-center`} data-testid="biometric-no-devices">
                <p className="text-[13px] text-[#5A6470]">لا تُوجَد أَجهِزَة مُسَجَّلَة بَعد.</p>
                <p className="text-[12px] text-[#9AA3AD] mt-1">فَعِّل البَصمَة لِلظُّهور هنا.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {devices.map((d) => (
                  <div
                    key={d.credential_id_b64}
                    className={`${CARD} px-3 py-2.5 flex items-center gap-3`}
                    data-testid={`biometric-device-${d.credential_id_b64.slice(0, 8)}`}
                  >
                    <span
                      className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                        d.disabled ? "bg-[#0A192F]/5 text-[#9AA3AD]" : "bg-[#FFF8E1] text-[#B8860B]"
                      }`}
                    >
                      <Fingerprint size={18} weight="duotone" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] font-bold text-[#0A192F] truncate flex items-center gap-1.5">
                        {d.device_name || "جِهاز"}
                        {d.disabled && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#0A192F]/8 text-[#5A6470]">
                            مُنتَهٍ
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-[#5A6470] truncate">
                        {d.last_used_at ? `آخِر استخدام: ${formatTime(d.last_used_at)}` : "لَم يُستَخدَم بَعد"}
                      </p>
                    </div>
                    {!d.disabled && (
                      <button
                        type="button"
                        onClick={() => onRevoke(d.credential_id_b64)}
                        disabled={revoking === d.credential_id_b64}
                        aria-label="إنهاء الجِهاز"
                        className="w-9 h-9 rounded-full bg-white border border-[#FECACA] text-[#B91C1C] flex items-center justify-center active:scale-95 transition-transform disabled:opacity-50"
                      >
                        {revoking === d.credential_id_b64
                          ? <Spinner size={14} className="animate-spin" />
                          : <Trash size={14} weight="bold" />}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Security note */}
          <p className="text-[11.5px] text-[#5A6470] text-center mt-4 px-2 leading-relaxed">
            <Check size={12} className="inline -mt-0.5 me-1 text-[#15803D]" />
            بَصمَتُك لا تُغادِر جِهازُك. الخادِم يَملِك مُفتاحاً عاماً فَقَط.
          </p>

          <div style={{ height: "24px" }} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

function formatTime(iso) {
  try {
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days === 0) return "اليَوم";
    if (days === 1) return "أَمس";
    if (days < 7) return `قَبل ${days} أَيّام`;
    if (days < 30) return `قَبل ${Math.floor(days / 7)} أسابيع`;
    return d.toLocaleDateString("ar-IQ");
  } catch {
    return "";
  }
}
